import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usersStorage } from '../../services/localStorage.service';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDemoCredentials, setShowDemoCredentials] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Initialize default admin user if no users exist
  useEffect(() => {
    usersStorage.initialize();
  }, []);

  // Function to fill demo credentials
  const fillDemoCredentials = () => {
    setFormData({
      email: 'admin@medicalcrm.com',
      password: 'admin@9975',
    });
    setError('');
    setShowDemoCredentials(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(''); // Clear error on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Authenticate user from localStorage
      const authenticatedUser = usersStorage.authenticate(formData.email, formData.password);
      
      if (authenticatedUser) {
        // Set authentication in localStorage
        localStorage.setItem('accessToken', 'dummy-token-for-local-login');
        localStorage.setItem('refreshToken', 'dummy-refresh-token');
        localStorage.setItem('user', JSON.stringify(authenticatedUser));
        localStorage.setItem('isAuthenticated', 'true');
        
        // Reload page to trigger AuthContext to update
        window.location.href = '/dashboard';
      } else {
        setError('Invalid email or password. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login. Please try again.');
      setIsLoading(false);
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-icon"></span>
            <span className="login-logo-text">
              <span className="login-logo-part1">ShreeSainath</span>
              <span className="login-logo-part2">MedicalCRM</span>
            </span>
          </div>
          <h2 className="login-title">Welcome Back!</h2>
          <p className="login-subtitle">Enter your credentials to access your account</p>
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setShowDemoCredentials(!showDemoCredentials)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: 'transparent',
                color: '#64748b',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              {showDemoCredentials ? 'Hide' : 'Show'} Demo Login Details
            </button>
            {showDemoCredentials && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#475569'
              }}>
                <div style={{ marginBottom: '8px', fontWeight: '600' }}>Demo Credentials:</div>
                <div style={{ marginBottom: '4px' }}>
                  <strong>Email:</strong> admin@medicalcrm.com
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Password:</strong> admin@9975
                </div>
                <button
                  type="button"
                  onClick={fillDemoCredentials}
                  style={{
                    padding: '6px 16px',
                    fontSize: '12px',
                    backgroundColor: '#14b8a6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Use These Credentials
                </button>
              </div>
            )}
          </div>
        </div>
        {error && (
          <div className="login-error" style={{ 
            color: '#ef4444', 
            padding: '10px', 
            marginBottom: '15px', 
            backgroundColor: '#fee2e2', 
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email / Username</label>
            <div className="input-wrapper">
              <span className="input-icon"></span>
              <input
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your username or email"
                required
              />
            </div>
          </div>
          <div className="form-group">
            <div className="form-group-header">
              <label htmlFor="password">Password</label>
              {/* <button type="button" className="forgot-password">
                Forgot password?
              </button> */}
            </div>
            <div className="input-wrapper" style={{ position: 'relative' }}>
              <span className="input-icon"></span>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your password"
                required
                style={{ paddingRight: '45px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b'
                }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.5 10C2.5 10 5 5 10 5C15 5 17.5 10 17.5 10C17.5 10 15 15 10 15C5 15 2.5 10 2.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.5 2.5L17.5 17.5M8.33333 8.33333C7.89131 8.77535 7.5 9.44102 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5C10.559 12.5 11.2246 12.1087 11.6667 11.6667M14.1667 14.1667C13.1087 14.7754 11.6667 15 10 15C5 15 2.5 10 2.5 10C3.33333 8.33333 4.58333 6.91667 6.25 5.83333M11.6667 4.16667C13.3333 5.08333 14.5833 6.5 15.4167 8.16667L17.5 10C17.5 10 15 15 10 15C9.16667 15 8.41667 14.9167 7.75 14.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
          {/* <div className="login-footer">
            <span>Don't have an account? </span>
            <button type="button" className="signup-link">Sign up</button>
          </div> */}
        </form>
      </div>
    </div>
  );
};

export default Login;
